import { useDashboard, useTeam } from '@vrplatform/data';
import {
  AppShell,
  LoadingOverlay,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { type JSX, type ReactNode, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ErrorBoundary, PmErrorIssueNotification } from '../components';
import { useNavbarExpanded } from '../hooks';
import {
  AuditEffectsModal,
  ExtractModal,
  JournalEntriesModal,
} from '../modals';
import { Header } from './Header';
import { OwnerImpersonationNotification } from './OwnerImpersonationNotification';
import { Navbar, type NavbarBaseProps } from './navbar';

export const AppLayout = ({
  children,
  drawers,
  ...navbarProps
}: {
  children: ReactNode;
  drawers: (() => JSX.Element)[];
} & NavbarBaseProps) => {
  const [{ isGL: GL }] = useTeam();

  const {
    isDesktopExpanded: isExpanded,
    isMobile,
    mobileBreakpoint,
  } = useNavbarExpanded();
  const [dashboard] = useDashboard();
  const { colorScheme } = useMantineColorScheme();
  const location = useLocation();
  const theme = useMantineTheme();
  const navBgColor =
    colorScheme === 'dark'
      ? theme.colors.dark[8]
      : theme.colors[theme.primaryColor][8];

  const [collapsedOnMobile, setCollapsedOnMobile] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!collapsedOnMobile) {
      setCollapsedOnMobile(true);
    }
  }, [location.search, location.pathname]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingOverlay visible />}>
        <AppShell
          header={{
            height: 60,
            collapsed: !isMobile,
          }}
          navbar={{
            collapsed: {
              desktop: false,
              mobile: true,
            },
            breakpoint: mobileBreakpoint,
            width: isExpanded ? 310 : 110,
          }}
          aside={{
            breakpoint: mobileBreakpoint,
            collapsed: {
              desktop: true,
              mobile: collapsedOnMobile,
            },
            width: isExpanded ? 280 : 110,
          }}
          styles={(theme) => ({
            main: {
              overflow: 'scroll',
            },
            navbar: {
              width: isExpanded ? 280 : 80,
              transition: 'all 0.3s ease-in-out',
              backgroundColor: navBgColor,
              height: `calc(100vh - 2*${theme.spacing.sm})`,
              margin: theme.spacing.sm,
              borderRadius: theme.spacing.md,
              overflowX: 'hidden',
              border: 'none',
            },
            aside: {
              width: isExpanded ? 280 : 80,
              transition: 'all 0.3s ease-in-out',
              backgroundColor: navBgColor,
              height: `calc(100vh - 2*${theme.spacing.sm} - 55px)`,
              margin: theme.spacing.sm,
              marginRight: collapsedOnMobile ? 0 : theme.spacing.sm,
              borderRadius: theme.spacing.md,
              overflowX: 'hidden',
              border: 'none',
            },
          })}
        >
          <Header
            toggleNavbar={(e) => setCollapsedOnMobile((c) => e ?? !c)}
            opened={!collapsedOnMobile}
          />

          {isMobile ? (
            <AppShell.Aside bg={navBgColor} c="white">
              <Navbar {...navbarProps} />
            </AppShell.Aside>
          ) : (
            <AppShell.Navbar bg={navBgColor} c="white">
              <Navbar {...navbarProps} />
            </AppShell.Navbar>
          )}

          <AppShell.Main
            mt={isMobile ? '60px' : undefined}
            py={isMobile ? 'xs' : 'sm'}
            pr={isMobile ? 'xs' : 'sm'}
            pl={isMobile ? 'xs' : undefined}
            display="flex"
            h="100vh"
            mih="100vh"
            mah="100vh"
            style={{
              flexDirection: 'column',
              position: 'relative',
              overflowX: 'hidden',
            }}
          >
            <ErrorBoundary>{children}</ErrorBoundary>
          </AppShell.Main>
        </AppShell>

        {/* // ** Dynamically added drawers depending on app */}
        {drawers.map((Drawer, i) => (
          <Drawer key={i} />
        ))}

        <OwnerImpersonationNotification />

        {dashboard === 'propertyManager' && !isMobile && (
          <PmErrorIssueNotification type="affix" />
        )}

        <ExtractModal />
        {GL && (
          <>
            <JournalEntriesModal />
            <AuditEffectsModal />
          </>
        )}
      </Suspense>
    </ErrorBoundary>
  );
};
